import React, { useState } from 'react';
import { apiService } from '../services/api';

const ChannelComparison = ({ onPageChange }) => {
  const [channelUrls, setChannelUrls] = useState(['', '']);
  const [numChannels, setNumChannels] = useState(2);
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [error, setError] = useState(null);

  const handleCompare = async () => {
    const urls = channelUrls.slice(0, numChannels).filter(url => url.trim());
    if (urls.length < 2) {
      setError('Please provide at least 2 channel URLs');
      return;
    }

    setIsComparing(true);
    setError(null);
    setComparisonResult(null);

    try {
      const result = await apiService.compareChannels(urls);
      if (result.success) {
        setComparisonResult(result.data);
      } else {
        throw new Error(result.error || 'Comparison failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to compare channels');
    } finally {
      setIsComparing(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Generate floating particles
  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981'];
    
    for (let i = 0; i < 50; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 4)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div className="page-container">
      {/* Animated Background Elements */}
      <div className="animated-background" />

      {/* Floating particles */}
      <div className="floating-particles">
        {generateParticles()}
      </div>

      <div className="page-content">
        {/* Hero Section */}
        <div className="hero-section">
          <h1 className="hero-title">
            Channel Comparison
          </h1>
          <p className="hero-subtitle">
            Compare 2-3 YouTube channels side-by-side
          </p>
        </div>

        {/* Input Section */}
        <div className="input-section">
          <div className="results-container" style={{ marginBottom: '2rem' }}>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Number of channels:</label>
            <select
              value={numChannels}
              onChange={(e) => {
                const num = parseInt(e.target.value);
                setNumChannels(num);
                setChannelUrls(prev => {
                  const newUrls = [...prev];
                  while (newUrls.length < num) newUrls.push('');
                  return newUrls.slice(0, num);
                });
              }}
              style={{
                padding: '0.5rem',
                background: 'rgba(40, 40, 40, 0.9)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: 'white'
              }}
            >
              <option value={2}>2 Channels</option>
              <option value={3}>3 Channels</option>
            </select>
          </div>

          {channelUrls.slice(0, numChannels).map((url, index) => (
            <div key={index} style={{ marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder={`Channel ${index + 1} URL or ID`}
                value={url}
                onChange={(e) => {
                  const newUrls = [...channelUrls];
                  newUrls[index] = e.target.value;
                  setChannelUrls(newUrls);
                }}
                disabled={isComparing}
                className="input-field"
                style={{
                  width: '100%',
                  marginBottom: '1rem'
                }}
              />
            </div>
          ))}

          <button
            onClick={handleCompare}
            disabled={isComparing || channelUrls.slice(0, numChannels).filter(u => u.trim()).length < 2}
            className="analyze-button"
            style={{
              width: '100%',
              marginTop: '1rem'
            }}
          >
            {isComparing ? (
              <>
                <span className="spinner" />
                Comparing...
              </>
            ) : (
              'Compare Channels'
            )}
          </button>

          {error && (
            <div className="error-container" style={{ marginTop: '1rem' }}>
              <p className="error-content">{error}</p>
            </div>
          )}
          </div>
        </div>

        {comparisonResult && (
          <div>
            <h2 className="hero-title" style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              marginBottom: '2rem'
            }}>
              Comparison Results
            </h2>

            <div className="results-container" style={{ marginBottom: '2rem' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${comparisonResult.channels.length}, 1fr)`,
                gap: '1.5rem'
              }}>
                {comparisonResult.channels.map((channel, index) => (
                  <div
                    key={channel.channel_id}
                    className="step-card"
                    style={{
                      textAlign: 'center'
                    }}
                  >
                    {channel.thumbnail && (
                      <img
                        src={channel.thumbnail}
                        alt={channel.channel_name}
                        style={{
                          width: '100px',
                          height: '100px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginBottom: '1rem',
                          border: '3px solid rgba(139, 92, 246, 0.5)'
                        }}
                      />
                    )}
                    <h3 className="hero-title" style={{
                      fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                      marginBottom: '1rem'
                    }}>
                      {channel.channel_name}
                    </h3>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: '900', color: '#8b5cf6' }}>
                        {formatNumber(channel.subscriber_count)}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Subscribers</div>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: '900', color: '#ec4899' }}>
                        {formatNumber(channel.total_views)}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Total Views</div>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: '900', color: '#06b6d4' }}>
                        {formatNumber(channel.video_count)}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Videos</div>
                    </div>

                    <div>
                      <div style={{ fontSize: '1.2rem', fontWeight: '900', color: '#10b981' }}>
                        {formatNumber(channel.avg_views_per_video)}
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Avg Views/Video</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="results-container">
              <h3 className="hero-title" style={{
                fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
                marginBottom: '1.5rem'
              }}>
                Comparison Summary
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem'
              }}>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Highest Subscribers
                  </div>
                  <div style={{ color: '#e5e7eb', fontSize: '1.1rem', fontWeight: '600' }}>
                    {comparisonResult.comparison_metrics.highest_subscribers.channel_name}
                  </div>
                  <div style={{ color: '#8b5cf6', fontSize: '0.9rem' }}>
                    {formatNumber(comparisonResult.comparison_metrics.highest_subscribers.subscriber_count)}
                  </div>
                </div>

                <div>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Highest Avg Views
                  </div>
                  <div style={{ color: '#e5e7eb', fontSize: '1.1rem', fontWeight: '600' }}>
                    {comparisonResult.comparison_metrics.highest_avg_views.channel_name}
                  </div>
                  <div style={{ color: '#10b981', fontSize: '0.9rem' }}>
                    {formatNumber(comparisonResult.comparison_metrics.highest_avg_views.avg_views_per_video)}
                  </div>
                </div>

                <div>
                  <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Most Videos
                  </div>
                  <div style={{ color: '#e5e7eb', fontSize: '1.1rem', fontWeight: '600' }}>
                    {comparisonResult.comparison_metrics.most_videos.channel_name}
                  </div>
                  <div style={{ color: '#06b6d4', fontSize: '0.9rem' }}>
                    {formatNumber(comparisonResult.comparison_metrics.most_videos.video_count)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelComparison;

